import React, { useState } from 'react';
import {
  AnalyticsRange,
  TeacherLoadingState,
  TeacherPageShell,
  useTeacherAdventureAiAnalytics,
} from './teacherAdventureShared';

const RANGE_OPTIONS: Array<{ value: AnalyticsRange; label: string }> = [
  { value: '24h', label: '24 小时' },
  { value: '7d', label: '7 天' },
  { value: '30d', label: '30 天' },
];

const formatPercent = (value: number) => `${Math.round(value * 100)}%`;
const formatMs = (value: number) => `${Math.round(value)}ms`;

export const TeacherAiAnalyticsPage: React.FC = () => {
  const [range, setRange] = useState<AnalyticsRange>('7d');
  const { analytics, isLoading, loadError } = useTeacherAdventureAiAnalytics(range);

  if (isLoading || loadError || !analytics) {
    return (
      <TeacherPageShell title="AI 分析" description="这里查看探险图片评估链路的成功率、错误率和模型命中情况。">
        <TeacherLoadingState error={loadError} loadingText="正在加载 AI 分析..." />
      </TeacherPageShell>
    );
  }

  const sections = [
    {
      key: 'danger',
      title: '险情图片',
      summary: analytics.summary.danger,
      timeline: analytics.timeline.danger,
      failures: analytics.failureBreakdown.danger,
      ocrModels: analytics.modelHitRates.danger.ocr,
      visionModels: analytics.modelHitRates.danger.directVision,
      pipelines: analytics.pipelineBreakdown.danger,
    },
    {
      key: 'escape',
      title: '逃生图片',
      summary: analytics.summary.escape,
      timeline: analytics.timeline.escape,
      failures: analytics.failureBreakdown.escape,
      ocrModels: analytics.modelHitRates.escape.ocr,
      visionModels: analytics.modelHitRates.escape.directVision,
      pipelines: analytics.pipelineBreakdown.escape,
    },
  ];

  return (
    <TeacherPageShell title="AI 分析" description="这里查看探险图片评估链路的成功率、错误率和模型命中情况。">
      <section className="flex flex-wrap gap-3">
        {RANGE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-label={option.label}
            onClick={() => setRange(option.value)}
            className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
              range === option.value
                ? 'bg-slate-900 text-white'
                : 'border border-stone-300 bg-white text-slate-700'
            }`}
          >
            {option.label}
          </button>
        ))}
      </section>

      <section className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-black text-slate-900">最近答疑摘要</h2>
            <p className="mt-1 text-sm text-slate-500">这里看学生最近向探险智多星请教了什么，方便你快速找到常见误区。</p>
          </div>
          <div className="rounded-2xl bg-amber-50 px-4 py-3 text-right">
            <div className="text-xs text-amber-700">最近记录</div>
            <div className="text-3xl font-black text-slate-900">{analytics.guideChatSummaries.length}</div>
          </div>
        </div>
        <div className="mt-5 grid gap-3 lg:grid-cols-2">
          {analytics.guideChatSummaries.length > 0 ? analytics.guideChatSummaries.map((item) => (
            <article key={`${item.studentId}-${item.updatedAt}`} className="rounded-[1.5rem] border border-amber-100 bg-amber-50/70 p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-lg font-black text-slate-900">{item.studentName}</div>
                  <div className="mt-1 text-xs text-slate-500">{item.updatedAt}</div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {item.misconceptionTags.map((tag) => (
                    <span key={`${item.studentId}-${tag}`} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-700">{item.summary}</p>
            </article>
          )) : (
            <div className="rounded-[1.5rem] border border-dashed border-stone-200 bg-stone-50 px-4 py-8 text-sm text-slate-500">
              当前时间范围内还没有新的答疑摘要。
            </div>
          )}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        {sections.map((section) => (
          <article key={section.key} className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black text-slate-900">{section.title}</h2>
                <p className="mt-1 text-sm text-slate-500">总运行数 {section.summary.totalRuns}</p>
              </div>
              <div className="rounded-2xl bg-stone-100 px-4 py-3 text-right">
                <div className="text-xs text-slate-500">成功率</div>
                <div className="text-3xl font-black text-slate-900">{formatPercent(section.summary.successRate)}</div>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-[1.25rem] bg-stone-50 p-4">
                <div className="text-xs text-slate-500">超时率</div>
                <div className="mt-2 text-xl font-black text-slate-900">{formatPercent(section.summary.timeoutRate)}</div>
              </div>
              <div className="rounded-[1.25rem] bg-stone-50 p-4">
                <div className="text-xs text-slate-500">JSON 失败率</div>
                <div className="mt-2 text-xl font-black text-slate-900">
                  {formatPercent(section.summary.jsonParseFailureRate)}
                </div>
              </div>
              <div className="rounded-[1.25rem] bg-stone-50 p-4">
                <div className="text-xs text-slate-500">平均耗时</div>
                <div className="mt-2 text-xl font-black text-slate-900">{formatMs(section.summary.avgDurationMs)}</div>
              </div>
              <div className="rounded-[1.25rem] bg-stone-50 p-4">
                <div className="text-xs text-slate-500">P95 耗时</div>
                <div className="mt-2 text-xl font-black text-slate-900">{formatMs(section.summary.p95DurationMs)}</div>
              </div>
            </div>

            <div className="mt-6 grid gap-4 xl:grid-cols-2">
              <div className="rounded-[1.5rem] border border-stone-200 p-4">
                <h3 className="text-sm font-bold text-slate-900">错误类型排行</h3>
                <div className="mt-3 space-y-2">
                  {section.failures.length > 0 ? section.failures.map((item) => (
                    <div key={`${section.key}-${item.errorType}`} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{item.errorType}</span>
                      <span className="font-semibold text-slate-900">
                        {item.count} / {formatPercent(item.ratio)}
                      </span>
                    </div>
                  )) : <div className="text-sm text-slate-500">当前没有失败记录。</div>}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-stone-200 p-4">
                <h3 className="text-sm font-bold text-slate-900">Pipeline 对比</h3>
                <div className="mt-3 space-y-2">
                  {section.pipelines.map((item) => (
                    <div key={`${section.key}-${item.pipelineMode}`} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{item.pipelineMode}</span>
                      <span className="font-semibold text-slate-900">
                        {item.totalRuns} 次 / {formatPercent(item.successRate)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-4 xl:grid-cols-2">
              <div className="rounded-[1.5rem] border border-stone-200 p-4">
                <h3 className="text-sm font-bold text-slate-900">OCR 模型命中率</h3>
                <div className="mt-3 space-y-2">
                  {section.ocrModels.length > 0 ? section.ocrModels.map((item) => (
                    <div key={`${section.key}-ocr-${item.model}`} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{item.model}</span>
                      <span className="font-semibold text-slate-900">
                        {item.count} / {formatPercent(item.ratio)}
                      </span>
                    </div>
                  )) : <div className="text-sm text-slate-500">当前没有 OCR 命中数据。</div>}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-stone-200 p-4">
                <h3 className="text-sm font-bold text-slate-900">Direct Vision 命中率</h3>
                <div className="mt-3 space-y-2">
                  {section.visionModels.length > 0 ? section.visionModels.map((item) => (
                    <div key={`${section.key}-vision-${item.model}`} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{item.model}</span>
                      <span className="font-semibold text-slate-900">
                        {item.count} / {formatPercent(item.ratio)}
                      </span>
                    </div>
                  )) : <div className="text-sm text-slate-500">当前没有 direct vision 命中数据。</div>}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-[1.5rem] border border-stone-200 p-4">
              <h3 className="text-sm font-bold text-slate-900">成功率与平均耗时趋势</h3>
              <div className="mt-3 space-y-2">
                {section.timeline.map((item) => (
                  <div key={`${section.key}-${item.bucket}`} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{item.bucket}</span>
                    <span className="font-semibold text-slate-900">
                      {formatPercent(item.successRate)} / {formatMs(item.avgDurationMs)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </article>
        ))}
      </section>
    </TeacherPageShell>
  );
};
